import { useTranslate } from "@tolgee/react";
import { Button } from "#/components/ui/button";
import { CheckboxIndicator } from "#/components/ui/checkbox.tsx";
import { Switch } from "#/components/ui/switch.tsx";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "#/components/ui/tooltip";
import { cn } from "#/lib/utils";
import {
	type TimeInForce,
	timeInForceOptions,
} from "./constants/order-form.constants.ts";
import { OrderFormSelect } from "./order-form-select.tsx";

type OrderFormOptionsProps = {
	reduceOnly: boolean;
	onReduceOnlyChange: (nextValue: boolean) => void;
	autoQuoteAvailable: boolean;
	showAutoQuote: boolean;
	autoQuote: boolean;
	onAutoQuoteChange: (nextValue: boolean) => void;
	showTimeInForce: boolean;
	timeInForce: TimeInForce;
	onTimeInForceChange: (nextValue: TimeInForce) => void;
};

export function OrderFormOptions({
	reduceOnly,
	onReduceOnlyChange,
	autoQuoteAvailable,
	showAutoQuote,
	autoQuote,
	onAutoQuoteChange,
	showTimeInForce,
	timeInForce,
	onTimeInForceChange,
}: OrderFormOptionsProps) {
	const { t } = useTranslate();

	const timeInForceDescriptions: Record<TimeInForce, string> = {
		GTC: t("form.tif.gtc-desc"),
		IOC: t("form.tif.ioc-desc"),
	};
	const timeInForceShortLabels: Record<TimeInForce, string> = {
		GTC: t("form.tif.gtc"),
		IOC: t("form.tif.ioc"),
	};
	const timeInForceSelectOptions = timeInForceOptions.map((option) => ({
		label: (
			<span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
				<span>{timeInForceShortLabels[option]}</span>
				<span
					data-slot="tif-desc"
					className="text-caption1 text-trade-text-secondary"
				>
					({timeInForceDescriptions[option]})
				</span>
			</span>
		),
		value: option,
	}));

	return (
		<div className="flex w-full flex-nowrap items-center gap-1.5 sm:flex-wrap sm:gap-2.5">
			<div className="flex flex-col gap-2 sm:gap-1.5 sm:flex-row">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => onReduceOnlyChange(!reduceOnly)}
					className={cn(
						"h-auto shrink-0 gap-1.5 p-0 text-trade-text-secondary hover:bg-transparent sm:gap-2 text-caption1-medium sm:text-label1-medium",
						reduceOnly && "text-trade-text",
					)}
				>
					<CheckboxIndicator checked={reduceOnly} />
					<span>{t("form.reduce")}</span>
				</Button>

				{showAutoQuote && (
					<div
						onClick={() => onAutoQuoteChange(!autoQuote)}
						className={cn(
							"flex items-center text-trade-text-secondary",
							"shrink-0 gap-1 text-caption1-medium sm:gap-2 sm:text-label1-medium cursor-pointer",
						)}
					>
						<Switch
							size="sm"
							aria-label={t("form.auto-last")}
							checked={autoQuote}
							disabled={!autoQuoteAvailable}
						/>
						<span className={cn(autoQuote && "text-trade-text")}>
						{t("form.auto-last")}
					</span>
					</div>
				)}
			</div>

			{showTimeInForce && (
				<div className="ml-auto w-[82px] min-w-0 shrink-0 sm:w-auto sm:min-w-28">
					<OrderFormSelect
						ariaLabel={t("form.tif")}
						triggerLabel={
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="underline decoration-dashed underline-offset-2">
										{t("form.tif-short")}
									</span>
								</TooltipTrigger>
								<TooltipContent side="top">
									{t("form.tif-description")}
								</TooltipContent>
							</Tooltip>
						}
						value={timeInForce}
						onValueChange={onTimeInForceChange}
						options={timeInForceSelectOptions}
						matchTriggerWidth={false}
						contentClassName="min-w-full"
						triggerClassName="h-7 gap-1 rounded-[8px] px-1.5 text-caption1-medium sm:h-9 sm:gap-2 sm:rounded-[10px] sm:px-2 sm:text-label1-bold [&_[data-slot=select-value]]:text-[11px] sm:[&_[data-slot=select-value]]:text-[13px] [&_[data-slot=select-value]_[data-slot=tif-desc]]:hidden [&_[data-slot=popover-trigger-icon]]:size-3 sm:[&_[data-slot=popover-trigger-icon]]:size-4"
					/>
				</div>
			)}
		</div>
	);
}
